// Shared, framework-agnostic helpers for per-day store availability.
//
// One source of truth for the three-mode model (Open / Returns only / Closed),
// imported by the CI date picker, the CI server-side quote validation, the
// operator soft-warning, AND the embeddable SDK. It lives under src/core/generic
// (not src/lib) ON PURPOSE: the SDK's Rollup build excludes src/lib/**, so a
// value module imported by the SDK must live somewhere the SDK build includes.
// Keep this file free of src/lib imports for the same reason.
//
// No date-library dependency; callers pass an already-computed weekday index
// (0 = Sunday .. 6 = Saturday, matching JS `Date.getDay()`).

export type DayAvailability = 'open' | 'returns_only' | 'closed';

// Structural shape of a single day's stored hours. Kept local (not imported from
// src/lib) so the SDK can bundle this module. `StoreHours` in
// ~/lib/stores/types/store is assignable to this.
export interface DayHours {
  openingTime?: string;
  closingTime?: string;
  isClosed?: boolean;
  availability?: DayAvailability;
}

// Any object carrying the seven optional day-hours fields. Lets these helpers
// accept a full `Store`, the operator `StoreMinimal`, or the CI `StoreData`
// without coupling to one concrete type.
export interface WeekHours {
  sundayHours?: DayHours | null;
  mondayHours?: DayHours | null;
  tuesdayHours?: DayHours | null;
  wednesdayHours?: DayHours | null;
  thursdayHours?: DayHours | null;
  fridayHours?: DayHours | null;
  saturdayHours?: DayHours | null;
}

// A per-calendar-date availability override (holiday / special date). Overrides
// the weekly day-of-week pattern for any date it covers. Dates are org-local
// calendar strings ('YYYY-MM-DD'), NOT UTC instants, so they sort
// lexicographically, so range/recurrence comparisons are plain string compares
// with no timezone drift. Structural mirror of `SpecialDate` in
// ~/lib/stores/types/store (kept here, not imported, so the SDK can bundle it).
// `openingTime`/`closingTime` are informational-only (the CI captures no time of
// day, so they are not enforced here); they ride along for display/parity with
// the weekly rows and are ignored by the resolver helpers below.
export interface SpecialDateRule {
  startDate: string; // 'YYYY-MM-DD' (single date: startDate === endDate)
  endDate: string; // 'YYYY-MM-DD'
  availability: DayAvailability;
  recurYearly: boolean; // match the same month+day each year (fixed dates only)
  label?: string;
  openingTime?: string; // 24h "HH:mm"; informational, not enforced
  closingTime?: string; // 24h "HH:mm"; informational, not enforced
}
export type SpecialDates = SpecialDateRule[];

// Index 0 = Sunday .. 6 = Saturday (JS getDay order).
const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const VALID_MODES: ReadonlySet<DayAvailability> = new Set<DayAvailability>([
  'open',
  'returns_only',
  'closed',
]);

/**
 * Resolve a day's availability mode from its stored hours, tolerating legacy
 * rows. Precedence:
 *   1. no hours at all (unconfigured / null column) -> 'closed'
 *   2. an explicit, valid `availability` field -> that value
 *   3. legacy binary flag: `isClosed === false` -> 'open', else 'closed'
 * This reproduces the rollout mapping (Open->Open, Closed->Closed, nothing
 * silently becoming Returns only) with no data migration.
 */
export function normalizeDayAvailability(
  hours?: DayHours | null,
): DayAvailability {
  if (!hours) return 'closed';
  if (hours.availability && VALID_MODES.has(hours.availability)) {
    return hours.availability;
  }
  return hours.isClosed === false ? 'open' : 'closed';
}

function canDepart(availability: DayAvailability): boolean {
  return availability === 'open';
}

function canReturn(availability: DayAvailability): boolean {
  return availability !== 'closed';
}

/**
 * True if the store has any per-day hours at all. Used to distinguish a store
 * that is deliberately configured (enforce availability) from one whose hours
 * were never set or didn't load (skip enforcement rather than treat every day
 * as Closed and block all bookings).
 */
export function hasConfiguredHours(store?: WeekHours | null): boolean {
  if (!store) return false;
  return Boolean(
    store.sundayHours ||
      store.mondayHours ||
      store.tuesdayHours ||
      store.wednesdayHours ||
      store.thursdayHours ||
      store.fridayHours ||
      store.saturdayHours,
  );
}

/**
 * The store's seven days as availability modes, indexed 0 = Sunday .. 6 = Saturday
 * so callers can index directly with `Date.getDay()`.
 */
export function getStoreWeekAvailability(store: WeekHours): DayAvailability[] {
  return [
    normalizeDayAvailability(store.sundayHours),
    normalizeDayAvailability(store.mondayHours),
    normalizeDayAvailability(store.tuesdayHours),
    normalizeDayAvailability(store.wednesdayHours),
    normalizeDayAvailability(store.thursdayHours),
    normalizeDayAvailability(store.fridayHours),
    normalizeDayAvailability(store.saturdayHours),
  ];
}

/** Weekday indices (0 = Sun) a customer may NOT depart on (not Open). */
export function getDisabledDepartureWeekdays(
  week: DayAvailability[],
): number[] {
  return week.reduce<number[]>((acc, mode, dow) => {
    if (!canDepart(mode)) acc.push(dow);
    return acc;
  }, []);
}

/** Weekday indices (0 = Sun) a customer may NOT return on (Closed). */
export function getDisabledReturnWeekdays(week: DayAvailability[]): number[] {
  return week.reduce<number[]>((acc, mode, dow) => {
    if (!canReturn(mode)) acc.push(dow);
    return acc;
  }, []);
}

/**
 * Day-name labels for the unavailable days, for the picker helper note and the
 * operator warning. `forDeparture` selects which constraint to report:
 * departure lists non-Open days, return lists Closed days.
 */
export function getUnavailableDayLabels(
  week: DayAvailability[],
  { forDeparture }: { forDeparture: boolean },
): string[] {
  const disabled = forDeparture
    ? getDisabledDepartureWeekdays(week)
    : getDisabledReturnWeekdays(week);
  return disabled.map((dow) => WEEKDAY_LABELS[dow]);
}

// Holidays & special dates (per-calendar-date overrides)

// Most-restrictive ordering: Closed beats Returns only beats Open. Used both to
// resolve overlapping special-date entries and as the precedence the spec
// mandates when two entries cover the same date.
const RESTRICTIVENESS: Record<DayAvailability, number> = {
  open: 0,
  returns_only: 1,
  closed: 2,
};

function moreRestrictive(
  a: DayAvailability,
  b: DayAvailability,
): DayAvailability {
  return RESTRICTIVENESS[a] >= RESTRICTIVENESS[b] ? a : b;
}

/**
 * True if a special-date rule covers the given calendar day.
 * `dayKey` is a 'YYYY-MM-DD' org-local string, exactly as the picker builds it
 * (toDayKey of a TZDate) so the comparison matches what is visually greyed.
 *
 * Non-recurring: inclusive [startDate, endDate] string comparison.
 * Recurring: compares only the 'MM-DD' slice (year-agnostic) and supports a
 * range that wraps the year boundary (e.g. Dec 28 -> Jan 3, where
 * startMd > endMd). A recurring single date is the startMd === endMd case.
 */
function specialDateRuleCovers(
  rule: SpecialDateRule,
  dayKey: string,
): boolean {
  if (!rule?.startDate || !rule?.endDate) return false;

  if (!rule.recurYearly) {
    return dayKey >= rule.startDate && dayKey <= rule.endDate;
  }

  const md = dayKey.slice(5); // 'MM-DD'
  const startMd = rule.startDate.slice(5);
  const endMd = rule.endDate.slice(5);
  if (startMd <= endMd) {
    return md >= startMd && md <= endMd;
  }
  // Wrapped range: matches at/after start OR at/before end.
  return md >= startMd || md <= endMd;
}

/**
 * Effective availability for a specific calendar day. A covering special-date
 * entry OVERRIDES the weekly pattern entirely; the weekly base is NOT folded
 * into the overlap, which is what lets an `open` special date re-enable a
 * normally-Closed weekday. When several entries cover the same day, the most
 * restrictive (Closed > Returns only > Open) wins. With no covering entry, the
 * weekly mode governs.
 *
 * `week` is the 0=Sun..6=Sat array from getStoreWeekAvailability; `weekdayIndex`
 * is that day's getDay() value (caller computes it in the org timezone).
 */
function resolveDayAvailability(
  week: DayAvailability[],
  weekdayIndex: number,
  specialDates: SpecialDates | null | undefined,
  dayKey: string,
): DayAvailability {
  const base = week[weekdayIndex] ?? 'closed';
  if (!specialDates || specialDates.length === 0) return base;

  let resolved: DayAvailability | null = null;
  for (const rule of specialDates) {
    if (!specialDateRuleCovers(rule, dayKey)) continue;
    resolved =
      resolved === null
        ? rule.availability
        : moreRestrictive(resolved, rule.availability);
  }
  return resolved ?? base;
}

/** Departure allowed on a specific date (special date overrides weekly). */
export function canDepartOnDate(
  week: DayAvailability[],
  weekdayIndex: number,
  specialDates: SpecialDates | null | undefined,
  dayKey: string,
): boolean {
  return canDepart(
    resolveDayAvailability(week, weekdayIndex, specialDates, dayKey),
  );
}

/** Return allowed on a specific date (special date overrides weekly). */
export function canReturnOnDate(
  week: DayAvailability[],
  weekdayIndex: number,
  specialDates: SpecialDates | null | undefined,
  dayKey: string,
): boolean {
  return canReturn(
    resolveDayAvailability(week, weekdayIndex, specialDates, dayKey),
  );
}

// Decompose a rule's calendar coverage into one or two 'MM-DD' [start, end]
// segments. A non-wrapping range is one segment; a wrapped recurring range
// (Dec 28 -> Jan 3) becomes [start..'12-31'] and ['01-01'..end].
function monthDaySegments(rule: SpecialDateRule): Array<[string, string]> {
  const startMd = rule.startDate.slice(5);
  const endMd = rule.endDate.slice(5);
  if (startMd <= endMd) return [[startMd, endMd]];
  return [
    [startMd, '12-31'],
    ['01-01', endMd],
  ];
}

function segmentsIntersect(
  a: Array<[string, string]>,
  b: Array<[string, string]>,
): boolean {
  for (const [as, ae] of a) {
    for (const [bs, be] of b) {
      if (as <= be && bs <= ae) return true;
    }
  }
  return false;
}

function specialDateRulesOverlap(
  a: SpecialDateRule,
  b: SpecialDateRule,
): boolean {
  if (!a?.startDate || !a?.endDate || !b?.startDate || !b?.endDate) {
    return false;
  }
  // Both fixed (non-recurring): exact inclusive interval intersection.
  if (!a.recurYearly && !b.recurYearly) {
    return a.startDate <= b.endDate && b.startDate <= a.endDate;
  }
  // At least one recurs: years are irrelevant once a side repeats annually, so
  // compare on month+day segments. A month+day collision means they coincide in
  // some year, and for a non-blocking warning that over-report is acceptable.
  return segmentsIntersect(monthDaySegments(a), monthDaySegments(b));
}

/**
 * Indices of special-date entries that overlap at least one other entry on some
 * calendar day. Drives the non-blocking overlap warning in the Edit Store UI;
 * never affects enforcement (which uses the precise per-date resolver above).
 */
export function getOverlappingSpecialDateIndices(
  rules: SpecialDates,
): Set<number> {
  const overlapping = new Set<number>();
  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      if (specialDateRulesOverlap(rules[i], rules[j])) {
        overlapping.add(i);
        overlapping.add(j);
      }
    }
  }
  return overlapping;
}
